import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getFieldPathsForType } from '@/utils/fieldPaths';
import type { LogisticsObjectScope } from '@/types/logisticsObject';

export interface FieldSelectorProps {
  value?: string;
  onChange: (field: string | null) => void;
  logisticsObjectType?: LogisticsObjectScope;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
  value,
  onChange,
  logisticsObjectType = 'all',
  disabled = false,
  error = false,
  helperText,
  size = 'small'
}) => {
  const fieldPaths = React.useMemo(
    () => getFieldPathsForType(logisticsObjectType),
    [logisticsObjectType]
  );

  // Track the input value separately from the committed value
  const [inputValue, setInputValue] = React.useState(value || '');

  // Sync input value when value prop changes from parent
  React.useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  return (
    <Autocomplete
      freeSolo
      options={fieldPaths as readonly string[]}
      value={value || null}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        // User selected from dropdown - commit the change
        onChange(newValue);
        setInputValue(newValue || '');
      }}
      onInputChange={(_, newValue) => {
        // User is typing - just update the input display
        setInputValue(newValue);
      }}
      onBlur={() => {
        // User finished editing - commit the change
        if (inputValue !== value) {
          onChange(inputValue || null);
        }
      }}
      disabled={disabled}
      size={size}
      slotProps={{
        paper: {
          sx: {
            width: 'max-content',
            maxWidth: '600px',
            minWidth: '300px'
          }
        },
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip'
            }
          }
        },
        clearIndicator: {
          sx: {
            visibility: 'visible'
          }
        },
        popupIndicator: {
          sx: {
            display: value ? 'none' : 'block'
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Field Path"
          placeholder="Enter field path"
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
